import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "feeling-dohnut-chart-wrapper" }
const _hoisted_2 = { class: "feeling-legend" }
const _hoisted_3 = { class: "legend-left" }
const _hoisted_4 = { class: "legend-name" }
const _hoisted_5 = { class: "legend-value" }

import Chart from "@/views/base/Chart/Chart.vue";
import type { EChartsOption } from "echarts";
import {
  computed,
  watch,
  ref,
  onMounted,
  onBeforeUnmount,
} from "vue";

// -- Types ------------------------------------------------------------------------
type FeelingItem = {
  code: number;
  name: string;
  value: number;
};

type FeelingResource = Record<
  number,
  {
    color: string;
    icon: string;
  }
>;

// -- props -----------------------------------------------------------------------
const mobileBreakpoint = 767;
// FeelingDoughnutChartは現状フォントサイズ用のpropsを持たないため、
// コンポーネント内部で画面幅を監視してSP/PCサイズを切り替える。

export default /*@__PURE__*/_defineComponent({
  __name: 'FeelingDoughnutChart',
  props: {
    title: { default: "" },
    items: { default: () => [] },
    resource: { default: () => ({
      1: { color: "#F8F687", icon: "/server/necs/images/icon_feeling_1.png" }, // 前向き
      2: { color: "#FBC9A4", icon: "/server/necs/images/icon_feeling_2.png" }, // 嬉しい
      3: { color: "#EF9595", icon: "/server/necs/images/icon_feeling_3.png" }, // 楽しい
      4: { color: "#F9C729", icon: "/server/necs/images/icon_feeling_4.png" }, // ワクワク
      5: { color: "#EEBEBE", icon: "/server/necs/images/icon_feeling_5.png" }, // 穏やか
      6: { color: "#F9B557", icon: "/server/necs/images/icon_feeling_6.png" }, // 充実
      7: { color: "#B0EF4F", icon: "/server/necs/images/icon_feeling_7.png" }, // 自信
      8: { color: "#8BDCB7", icon: "/server/necs/images/icon_feeling_8.png" }, // 貢献感
      9: { color: "#83C6E3", icon: "/server/necs/images/icon_feeling_9.png" }, // 不安
      10: { color: "#AFABB2", icon: "/server/necs/images/icon_feeling_10.png" }, // 無力感
      11: { color: "#90AFA7", icon: "/server/necs/images/icon_feeling_11.png" }, // もやもや
      12: { color: "#6D68B7", icon: "/server/necs/images/icon_feeling_12.png" }, // イライラ
      13: { color: "#A3C6E3", icon: "/server/necs/images/icon_feeling_13.png" }, // 寂しい
      14: { color: "#8E63AB", icon: "/server/necs/images/icon_feeling_14.png" }, // 恐い
    }) }
  },
  setup(__props: any) {

const props = __props;

// -- Constants & Variable --------------------------------------------------
const img = new Image();
const imgWidth = ref(0);
const imgHeight = ref(0);
const viewportWidth = ref(window.innerWidth);
const chartHeight = computed(() => ({
  height: "300px",
}));

// --- Vue lifesycle hooks --------------------------------------------------

// valueの最大値を持つレコードを得る
const maxItem = computed(() => {
  if (!props.items.length) return null;
  return props.items.reduce(
    (prev, cur) => (cur.value > prev.value ? cur : prev),
    props.items[0]
  );
});

// 画像の読み込み
watch(
  () => maxItem.value,
  (item) => {
    if (!item) return;
    img.src = props.resource![item.code].icon;
  },
  { immediate: true }
);

// 画像の読み込み完了時
img.onload = () => {
  imgWidth.value = img.width;
  imgHeight.value = img.height;
};

const handleResize = () => {
  viewportWidth.value = window.innerWidth;
};

// チャートオプション
const doughnutOption = computed<EChartsOption>(() => {
  // SP: タイトル16px / 右ラベル14px、PC: タイトル18px / 右ラベル16px
  const isMobile = viewportWidth.value <= mobileBreakpoint;
  const titleFontSize = isMobile ? "16px" : "18px";
  const trendFontSize = "14px";

  const chartHeightPx = Number(chartHeight.value.height.replace("px", ""));
  const centerYPx = chartHeightPx * 0.54;
  const centerYPercent = (centerYPx / chartHeightPx) * 100;
  const imageTop = centerYPx - imgHeight.value / 2;

  // チャートデータ
  const chartData = props.items.map((i) => {
    const res = props.resource![i.code];
    return {
      name: i.name,
      value: i.value,
      itemStyle: { color: res.color },
    };
  });

  let graphicElements: any[] = [];
  if (imgWidth.value > 0) {
    graphicElements = [
      {
        type: "image",
        left: "center",
        top: imageTop,
        z: 100,
        style: {
          image: img,
          width: 78,
          height: 65,
        },
      },
    ];
  }

  return {
    title: [
      // タイトル
      {
        text: props.title,
        left: 0,
        top: 0,
        textStyle: {
          fontFamily: "Zen Maru Gothic, sans-serif",
          fontSize: titleFontSize,
          color: "#4e4e4e",
          fontWeight: "normal",
          lineHeight: 22,
        },
      },
      // 傾向
      {
        text: `多い傾向：{mo|${maxItem.value?.name || ""}}`,
        right: 0,
        top: 5,
        padding: 0,
        textStyle: {
          fontFamily:
            "Noto Sans JP,メイリオ,Meiryo,Hiragino Sans,Hiragino Kaku Gothic ProN,ヒラギノ角ゴ ProN,Osaka,ＭＳ Ｐゴシック,sans-serif",
          fontSize: trendFontSize,
          color: "#4e4e4e",
          fontWeight: "600",
          lineHeight: 22,
          rich: {
            mo: {
              padding: [2, 0, 2, 6],
            },
          },
        },
      },
    ],
    // 凡例
    legend: { show: false },
    // 画像
    graphic: {
      elements: graphicElements as any,
    },
    // チャートデータ
    series: [
      {
        type: "pie",
        radius: ["70%", "85%"],
        center: ["50%", `${centerYPercent}%`],
        startAngle: 90,
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        data: chartData,
        emphasis: { scale: false },
      },
    ],
    // ツールチップ
    tooltip: {
      trigger: "item",
      formatter: "{b}: {c}%",
    },
  };
});

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Chart, {
      option: doughnutOption.value,
      style: _normalizeStyle(chartHeight.value),
      autoResize: true,
      class: "chart-area"
    }, null, 8, ["option", "style"]),
    _createElementVNode("ul", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.items, (item) => {
        return (_openBlock(), _createElementBlock("li", {
          key: item.code
        }, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("span", {
              class: "legend-icon",
              style: _normalizeStyle({ background: _ctx.resource[item.code].color })
            }, null, 4),
            _createElementVNode("span", _hoisted_4, _toDisplayString(item.name), 1)
          ]),
          _createElementVNode("span", _hoisted_5, _toDisplayString(item.value) + "%", 1)
        ]))
      }), 128))
    ])
  ]))
}
}

})